import { Mixpanel } from "lib/mixpanel/mixpanel";
import { MIXPANEL_TOKEN } from "./constants";
import { postRequest } from "./fetchers/fetcher";
import { GTMEvent, TrackingAttributes } from "./types";
import { getUserId } from "./store/user";
import { datadogLogger } from "lib/datadog/logger";
import {
  getZephrUserProfile,
  updateZephrUserProfile,
} from "lib/fetchers/zephr/customFields";
import type { ZephrUserProfileUpdate } from "lib/zephr/profile-types";

// Environment checkers
export const isProduction = ["production", "prod"].includes(
  (process.env.ENVIRONMENT_NAME || "").toLowerCase()
);
export const isStaging = process.env.ENVIRONMENT_NAME === "staging";
export const isDevelopment = ["development", "dev"].includes(
  (process.env.ENVIRONMENT_NAME || "").toLowerCase()
);

export const trackGTMEvent = (GTMObject: GTMEvent): void => {
  (window as any).dataLayer = (window as any).dataLayer || [];
  if (GTMObject.event) {
    (window as any).dataLayer.push({ ...GTMObject });
  }
};

export const postTalksEvent = ({
  fields,
}: {
  fields: TrackingAttributes;
}): Promise<any> => {
  let resolvePromise: { (arg0): void } | null = null;

  const promise = new Promise((resolve, reject) => {
    resolvePromise = resolve;
  });

  window.tp = window.tp || [];
  window.tp.push([
    "init",
    () => {
      const userCookie = window?.tp?.userId?.getToken();

      const params = new URLSearchParams({
        userCookie,
        ...fields,
      });

      const url = `/api/subscriptionUtils/tracking/talks?${params}`;

      resolvePromise?.(postRequest(url, fields));
    },
  ]);
  return promise;
};

const FIELD_DELIMITER = ";";

export const parseDelimitedIds = (value: unknown): string[] => {
  if (typeof value !== "string" || value.trim().length === 0) {
    return [];
  }

  const delimiter = value.includes(FIELD_DELIMITER) ? FIELD_DELIMITER : ",";

  return value
    .split(delimiter)
    .map((id) => id.trim())
    .filter(Boolean);
};

export const stringifyDelimitedIds = (ids: string[]): string =>
  ids.join(FIELD_DELIMITER);

const REPORTS_DOWNLOADED_FIELD = "reports_downloaded";

export const trackReportDownload = async (reportId: string): Promise<void> => {
  try {
    // Fetch FRESH data from Zephr to avoid overwriting existing values.
    // This is critical because concurrent downloads (e.g., in multiple tabs or rapid user actions)
    // could result in stale cached data. By always fetching the latest profile, we ensure that
    // new downloads are appended to the current list, preventing a race condition where
    // previous downloads could be lost if we overwrite with outdated data.
    const userProfile = await getZephrUserProfile();
    const currentDownloadValue = userProfile?.[REPORTS_DOWNLOADED_FIELD];
    const currentDownloadIds = parseDelimitedIds(currentDownloadValue);

    if (!currentDownloadIds.includes(reportId)) {
      const newDownloadIds = Array.from(
        new Set([...currentDownloadIds, reportId])
      );

      const updatePayload: ZephrUserProfileUpdate = {
        [REPORTS_DOWNLOADED_FIELD]: stringifyDelimitedIds(newDownloadIds),
      };

      const result = await updateZephrUserProfile(updatePayload);

      if (!result.success) {
        throw new Error("Failed to update Zephr profile with report download.");
      }
    }
  } catch (error) {
    datadogLogger.error(
      "Error recording report download in Zephr",
      { reportId },
      error
    );
    // Don't throw - we don't want to block the download if Zephr update fails
  }
};

export type UpdatePaywallCounterProps = {
  type: "ar" | "sc" | "br" | "re" | "ev" | "ranking" | "leaderboard";
  timestamp: number | string;
};

export const updatePaywallCounter = ({
  type,
  timestamp,
}: UpdatePaywallCounterProps): Promise<any> => {
  let resolvePromise: { (arg0): void } | null = null;

  const promise = new Promise((resolve, reject) => {
    resolvePromise = resolve;
  });

  window.tp = window.tp || [];
  window.tp.push([
    "init",
    () => {
      const userCookie = window?.tp?.userId?.getToken();

      const params = new URLSearchParams({
        userCookie,
        type,
        timestamp: timestamp.toString(),
      });

      const url = `/api/subscriptionUtils/tracking/paywallHit?${params}`;

      resolvePromise?.(postRequest(url, {}));
    },
  ]);

  return promise;
};

export const trackEventOnMixpanel = ({
  eventName,
  fields,
}: {
  eventName: string;
  fields: TrackingAttributes;
}): void => {
  const mixpanel = new Mixpanel(MIXPANEL_TOKEN);
  const id = getUserId();

  if (id) {
    mixpanel.track(eventName, {
      distinct_id: id,
      ...fields,
    });
  }
};

export const slugify = (title: string) => {
  return title
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/[^\w-]+/g, "") // Remove all non-word chars
    .replace(/--+/g, "-") // Replace multiple - with single -
    .replace(/^-+/, "") // Trim - from start of text
    .replace(/-+$/, ""); // Trim - from end of text
};

const CENTRAL_EUROPEAN = { standard: "CET", daylight: "CEST" };
const EASTERN_EUROPEAN = { standard: "EET", daylight: "EEST" };

const ZONE_ABBREVIATIONS: Record<
  string,
  { standard: string; daylight: string }
> = {
  UTC: { standard: "UTC", daylight: "UTC" },
  "Europe/London": { standard: "GMT", daylight: "BST" },
  "Europe/Dublin": { standard: "GMT", daylight: "IST" },
  "Europe/Lisbon": { standard: "WET", daylight: "WEST" },
  "Europe/Amsterdam": CENTRAL_EUROPEAN,
  "Europe/Berlin": CENTRAL_EUROPEAN,
  "Europe/Brussels": CENTRAL_EUROPEAN,
  "Europe/Budapest": CENTRAL_EUROPEAN,
  "Europe/Copenhagen": CENTRAL_EUROPEAN,
  "Europe/Madrid": CENTRAL_EUROPEAN,
  "Europe/Oslo": CENTRAL_EUROPEAN,
  "Europe/Paris": CENTRAL_EUROPEAN,
  "Europe/Prague": CENTRAL_EUROPEAN,
  "Europe/Rome": CENTRAL_EUROPEAN,
  "Europe/Stockholm": CENTRAL_EUROPEAN,
  "Europe/Vienna": CENTRAL_EUROPEAN,
  "Europe/Warsaw": CENTRAL_EUROPEAN,
  "Europe/Zurich": CENTRAL_EUROPEAN,
  "Europe/Athens": EASTERN_EUROPEAN,
  "Europe/Bucharest": EASTERN_EUROPEAN,
  "Europe/Helsinki": EASTERN_EUROPEAN,
  "Europe/Kyiv": EASTERN_EUROPEAN,
  "Europe/Riga": EASTERN_EUROPEAN,
  "Europe/Sofia": EASTERN_EUROPEAN,
  "Europe/Tallinn": EASTERN_EUROPEAN,
  "Europe/Vilnius": EASTERN_EUROPEAN,
  "America/Chicago": { standard: "CST", daylight: "CDT" },
  "America/Denver": { standard: "MST", daylight: "MDT" },
  "America/Los_Angeles": { standard: "PST", daylight: "PDT" },
  "America/New_York": { standard: "EST", daylight: "EDT" },
  "Asia/Dubai": { standard: "GST", daylight: "GST" },
  "Asia/Kolkata": { standard: "IST", daylight: "IST" },
  "Asia/Singapore": { standard: "SGT", daylight: "SGT" },
  "Asia/Tokyo": { standard: "JST", daylight: "JST" },
};

const getUtcOffsetMinutes = (dateTime: Date, timeZone: string): number => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(dateTime);

  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((entry) => entry.type === type)?.value ?? "0");

  const asUtc = Date.UTC(
    part("year"),
    part("month") - 1,
    part("day"),
    part("hour"),
    part("minute"),
    part("second")
  );

  return Math.round((asUtc - dateTime.getTime()) / 60000);
};

const getOffsetLabel = (offsetMinutes: number): string => {
  if (offsetMinutes === 0) {
    return "GMT";
  }

  const sign = offsetMinutes > 0 ? "+" : "-";
  const absolute = Math.abs(offsetMinutes);
  const hours = Math.floor(absolute / 60);
  const minutes = absolute % 60;

  return minutes === 0
    ? `GMT${sign}${hours}`
    : `GMT${sign}${hours}:${String(minutes).padStart(2, "0")}`;
};

export const getTimeZoneString = (
  dateTime: Date,
  timeZone = "Europe/London"
) => {
  const abbreviations = ZONE_ABBREVIATIONS[timeZone];
  const offset = getUtcOffsetMinutes(dateTime, timeZone);

  if (!abbreviations) {
    return getOffsetLabel(offset);
  }

  const year = dateTime.getUTCFullYear();
  const standardOffset = Math.min(
    getUtcOffsetMinutes(new Date(Date.UTC(year, 0, 1)), timeZone),
    getUtcOffsetMinutes(new Date(Date.UTC(year, 6, 1)), timeZone)
  );

  return offset === standardOffset
    ? abbreviations.standard
    : abbreviations.daylight;
};

export const setCssVariable = (name: string, value: string | null) => {
  document.documentElement.style.setProperty(name, value);
};

export function getHumanFriendlyNumber(n: number): string {
  const nf = new Intl.NumberFormat("en-GB");
  return nf.format(Number(n));
}

export const getDateFormattedNumeric = (timestamp: number | string) => {
  const date = new Date(timestamp).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });

  return date.replaceAll("/", ".");
};

// Output: 2024-10-31
export const getISODateFormatted = (timestamp: number | string) => {
  let result: string | null = null;

  try {
    result = new Date(timestamp).toISOString().split("T")[0];
  } catch (e) {
    result = null;
  }

  return result;
};

// Output: Fri 21 Feb 2024
export const formattedDate = (
  date: Date,
  timezone = "Europe/London"
): string => {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
    .format(date)
    .replace(/,/g, "");
};

export const formattedDayMonth = (
  timestamp: number | string | null | undefined,
  timeZone = "Europe/London"
): string | undefined => {
  if (timestamp === null || timestamp === undefined) return undefined;

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) return undefined;

  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    day: "numeric",
    month: "short",
  }).format(date);
};

// Output: 14:00
export const formattedTime = (
  date: Date,
  timeZone = "Europe/London"
): string => {
  const formatted = new Intl.DateTimeFormat("en-GB", {
    timeZone: timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);

  const [hours, minutes] = formatted.split(":");
  // Check if hours is a single digit, if so, prepend '0' to make it two digits
  const formattedHours = hours.length === 1 ? "0" + hours : hours;

  return `${formattedHours}:${minutes}`;
};

export const timeString = (time: Date, timeZone = "Europe/London") =>
  `${new Intl.DateTimeFormat("en-GB", {
    timeZone: timeZone,
    hour: "numeric",
    minute: "2-digit",
  }).format(time)}`;

// Output format: "Tuesday, 18 January 2022"
export const getDateStringWeekday = (dateTime: Date) => {
  return `${dateTime.toLocaleDateString("en-GB", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  })}`;
};

// Output format: "12:30→13:30 GMT / 13:30→14:30 CET"
export const upcomingStartEndString = (startTime: Date, endTime: Date) => {
  return `${formattedTime(startTime)}→${formattedTime(endTime)} ${getTimeZoneString(
    startTime
  )} / ${formattedTime(startTime, "Europe/Brussels")}→${formattedTime(
    endTime,
    "Europe/Brussels"
  )} ${getTimeZoneString(startTime, "Europe/Brussels")}`;
};

export const formatTime = (time: string | Date | number) => {
  // This regex changes 2016-09-17T00:15:00.000Z to 20160917T001500Z
  return new Date(time).toISOString().replace(/-|:|\.\d\d\d/g, "");
};

export const cleanSlugsFromDecodedQueryParameters = (
  rawSlugArray: string[]
) => {
  return rawSlugArray.map((rawSlug) => {
    return (
      new URL(rawSlug, "https://sifted.eu").pathname.split("/").pop() || rawSlug
    );
  });
};

export const ensureValidUrlForWebsiteUrl = (link: string) => {
  if (link.startsWith("http://") || link.startsWith("https://")) {
    return link;
  }

  if (link.includes(".")) {
    return `https://${link}`;
  }

  return link;
};

/**
 * Builds a pathname with query parameters, excluding specified params and Next.js internal params.
 * Useful for constructing clean URLs while preserving marketing/tracking parameters.
 *
 * @param resolvedUrl - The full resolved URL from Next.js (e.g., "/articles/slug?param=value")
 * @param queryParams - The query object from Next.js context
 * @param excludeParams - Array of param keys to exclude (in addition to 'slug' which is always excluded)
 * @returns The pathname with filtered query string
 *
 * @example
 * ```ts
 * const pathname = buildPathnameWithQueryParams(
 *   "/articles/my-post?gift_token=abc&utm_source=email",
 *   { slug: ["my-post"], gift_token: "abc", utm_source: "email" },
 *   ["gift_token"]
 * );
 * // Returns: "/articles/my-post?utm_source=email"
 * ```
 */
export const buildPathnameWithQueryParams = (
  resolvedUrl: string,
  queryParams: Record<string, string | string[] | undefined>,
  excludeParams: string[] = []
): string => {
  // Always exclude Next.js internal 'slug' param
  const paramsToExclude = new Set(["slug", ...excludeParams]);

  const queryString = new URLSearchParams(
    Object.entries(queryParams)
      .filter(([key]) => !paramsToExclude.has(key))
      .reduce((acc, [key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => acc.append(key, v));
        } else if (value) {
          acc.append(key, value as string);
        }
        return acc;
      }, new URLSearchParams())
  ).toString();

  const basePath = resolvedUrl.split("?")[0];
  return basePath + (queryString ? `?${queryString}` : "");
};

export * from "./sharedUtils/briefings";
